import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

// Types

/**
 * Unified tracking request interface (SDK v6.0.291+)
 * Single endpoint for all tracking scenarios
 */
export interface UnifiedTrackingRequest {
  // Core IDs (course_id is always required)
  course_id: string;
  module_id?: string;
  lesson_id?: string;

  // Assessment tracking
  assessment_id?: string;
  assessment_type?: 'PRE' | 'POST'; // API expects simple 'PRE' or 'POST'

  // Progress tracking
  time_spent_seconds?: number;
  progress_percentage?: number;
  status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';

  // Assessment results (stage 2)
  score?: number;
  total_questions?: number;
  correct_answers?: number;
  passed?: boolean;
  attempt_number?: number;
  assessment_score?: number;        // Raw score (e.g., 45 out of 100)
  assessment_percentage?: number;   // Percentage score (0-100)
  assessment_passed?: boolean;      // Did user pass?
  assessment_attempts?: number;     // Current attempt number

  // Content interaction
  notes?: string;
  bookmarked?: boolean;
  quiz_answers?: Record<string, any>;  // Format: { question_id: answer_data }
  metadata?: Record<string, any>;
}

export interface DashboardData {
  total_courses: number;
  completed_courses: number;
  in_progress_courses: number;
  mandatory_courses: number;
  total_time_spent_hours: number;
  average_progress: number;
  upcoming_deadlines: Array<{
    course_id: string;
    course_title: string;
    deadline_date: string;
    progress_percentage: number;
  }>;
  recent_activity: Array<{
    course_id: string;
    course_title: string;
    last_accessed_at: string;
    progress_percentage: number;
  }>;
}

export interface ModuleProgress {
  module_id: string;
  module_title: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  progress_percentage: number;
  time_spent_seconds: number;
  last_accessed_at?: string;
  completed_at?: string;
  lesson_progress?: LessonProgress[];
}

export interface CourseWithProgress {
  course_id: string;
  course_title: string;
  description: string;
  difficulty: string;
  stage: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  estimated_duration: {
    hours: number;
    minutes: number;
    total_minutes: number;
  };
  rating?: number;
  assignment_id?: string;
  createdAt: string;
  updatedAt: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  progress_percentage: number;
  is_mandatory: boolean;
  deadline_date?: string;
  last_accessed_at?: string;
  total_time_spent_seconds: number;
  completed_modules: number;
  total_modules: number;
  // Fields for "Continue Where You Left Off"
  current_module_id?: string;
  current_content_item_id?: string;
  module_progress?: ModuleProgress[];
  started_at?: string;
  completed_at?: string;
}

// Enhanced lesson progress interface
export interface LessonProgress {
  lesson_id: string;
  lesson_title: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  progress_percentage: number;
  started_at?: string;
  last_accessed_at?: string;
  completed_at?: string;
  time_spent_seconds: number;
  score?: number;
  attempts: number;
  notes?: string;
  bookmarked: boolean;
  quiz_attempts?: Array<{
    attempt_number: number;
    score: number;
    answers: Record<string, any>;
    completed_at: string;
  }>;
  metadata?: Record<string, any>;
}

export interface DetailedProgress {
  category_name: string;
  estimated_duration: any;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
  _id: string;
  assignment_id: string;
  course_id: string;
  course_title: string;
  user_id: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  progress_percentage: number;
  total_time_spent_seconds: number;
  created_at?: string;
  started_at?: string;
  last_accessed_at?: string;
  completed_at?: string;
  is_mandatory: boolean;
  deadline_date?: string | null;
  module_progress: Array<{
    module_id: string;
    module_title: string;
    status: string;
    progress_percentage: number;
    time_spent_seconds: number;
    started_at?: string;
    last_accessed_at?: string;
    completed_at?: string;
    lesson_progress?: LessonProgress[];
  }>;
  total_modules: number;
  completed_modules: number;
  current_module_id?: string;
  current_content_item_id?: string;
  certificate_issued: boolean;
  certificate_id?: string;
  score?: number;
  attempts: number;
}

export interface TrackingSummary {
  not_started: number;
  in_progress: number;
  completed: number;
  expired: number;
  completion_percentage: number;
}

// Filter types for getUserProgress POST request
export type TrackingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
export type CourseTrackingSortField = 'last_accessed_at' | 'progress_percentage' | 'deadline_date' | 'created_at';

export interface UserProgressFilters {
  course_id?: string;
  search?: string;
  status?: TrackingStatus;
  assignment_id?: string;
  sort_by?: CourseTrackingSortField;
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ProgressSummary {
  total_not_started: number;
  total_in_progress: number;
  total_completed: number;
  total_expired: number;
}

export interface PaginatedProgressResponse {
  data: DetailedProgress[];
  total: number;
  page: number;
  limit: number;
  summary: ProgressSummary;
}

export interface UserCourseMetrics {
  total_courses: number;
  mandatory_courses: number;
  optional_courses: number;
  completed_courses: number;
  in_progress_courses: number;
  not_started_courses: number;
  expired_courses: number;
  avg_progress_percentage: number;
  total_time_invested_seconds: number;
  completion_rate: number;
  mandatory_completion_rate: number;
}

// Certification Tracking Types
export type CertificationTrackingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
export type CertificationTrackingSortField = 'LAST_ACCESSED' | 'PROGRESS' | 'DEADLINE' | 'CREATED_AT';

export interface CertificationTrackingFilters {
  page?: number;
  limit?: number;
  certification_id?: string;
  search?: string;
  status?: CertificationTrackingStatus;
  assignment_id?: string;
  sort_by?: CertificationTrackingSortField;
  sort_order?: 'asc' | 'desc';
}

export interface CertificationTracking {
  tracking_id: string;
  certification_id: string;
  title: string;
  description?: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  assignment_id: string;
  is_mandatory: boolean;
  deadline_date?: string | null;
  assigned_date?: string;
  assigned_by?: string;
  status: CertificationTrackingStatus;
  enrolled_at?: string;
  started_at?: string;
  completed_at?: string;
  last_accessed_at?: string;
  total_time_spent_seconds: number;
  exam_attempts: number;
  passed_exam: boolean;
  certificate_issued?: boolean;
  latest_exam_percentage: number;
  created_at?: string;
}

export interface CertificationProgressSummary {
  total_not_started: number;
  total_in_progress: number;
  total_completed: number;
  total_expired: number;
  total_passed: number;
}

export interface PaginatedCertificationResponse {
  data: CertificationTracking[];
  total: number;
  page: number;
  limit: number;
  summary: CertificationProgressSummary;
}

export interface UserCertificationMetrics {
  total_certifications: number;
  mandatory_count: number;
  passed_count: number;
  avg_progress: number;
  in_progress_count: number;
  not_started_count: number;
  expired_count: number;
  total_time_invested: number;
}

class TrackingService {
  /**
   * Unified tracking method (SDK v6.0.291+)
   * Single endpoint for all tracking scenarios:
   * - Course-level tracking (course_id only)
   * - Module-level tracking (course_id + module_id)
   * - Content-item tracking (course_id + module_id + lesson_id)
   * - Assessment tracking (course_id + assessment_id + assessment_type)
   *
   * Backend automatically:
   * - Routes to correct tracking level
   * - Rolls up time through hierarchy
   * - Handles assessment knowledge gain calculation
   * - Updates status automatically
   */
  async trackProgress(data: UnifiedTrackingRequest): Promise<DetailedProgress> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COURSE_TRACKING.UPDATE_LESSON_PROGRESS, data);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to track progress');
    } catch (error: any) {
      console.error('Error tracking progress:', error);
      throw error;
    }
  }

  /**
   * Update progress for a specific user (Admin only)
   */
  async updateUserProgress(userId: string, data: ProgressUpdate): Promise<DetailedProgress> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COURSE_TRACKING.UPDATE_USER_PROGRESS(userId), data);
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.description || 'Failed to update user progress');
    } catch (error: any) {
      console.error('Error updating user progress:', error);
      throw error;
    }
  }

  /**
   * Get dashboard data for the current user
   */
  async getDashboard(): Promise<DashboardData> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSE_TRACKING.MY_DASHBOARD);
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.description || 'Failed to fetch dashboard data');
    } catch (error: any) {
      console.error('Error fetching dashboard:', error);
      throw error;
    }
  }

  /**
   * Get courses with progress for the current user
   */
  async getMyCourses(): Promise<CourseWithProgress[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSE_TRACKING.MY_COURSES);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to fetch courses');
    } catch (error: any) {
      console.error('Error fetching my courses:', error);
      throw error;
    }
  }

  /**
   * Get detailed progress for a specific course (current user)
   * Returns full course structure with modules, content items, and assessments
   */
  async getMyCourseDetails(courseId: string): Promise<DetailedProgress> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSE_TRACKING.MY_COURSE_DETAILS(courseId));

      if (response.data.success || response.data.statusCode === 200) {
        return response.data.data;
      }

      throw new Error(response.data.message || response.data.description || 'Failed to fetch course details');
    } catch (error: any) {
      console.error('Error fetching course details:', error);
      throw error;
    }
  }

  /**
   * Get detailed progress for a specific user (POST with filters)
   * POST /skillzen-srv/api/course-tracking/user/{userId}
   */
  async getUserProgress(
    userId: string,
    filters?: UserProgressFilters
  ): Promise<PaginatedProgressResponse | DetailedProgress> {
    try {
      const payload: UserProgressFilters = {
        ...filters,
        sort_order: filters?.sort_order || 'desc'  // Default to desc
      };

      const response = await apiClient.post(
        API_ENDPOINTS.COURSE_TRACKING.GET_USER_PROGRESS(userId),
        payload
      );

      if (response.data.success) {
        // When course_id is provided, API returns a single object
        // Otherwise returns paginated response with data array
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to fetch user progress');
    } catch (error: any) {
      console.error('Error fetching user progress:', error);
      throw error;
    }
  }

  /**
   * Get user course summary
   */
  async getUserSummary(userId: string): Promise<TrackingSummary> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSE_TRACKING.USER_SUMMARY(userId));

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to fetch user summary');
    } catch (error: any) {
      console.error('Error fetching user summary:', error);
      throw error;
    }
  }

  /**
   * Get user course metrics
   * GET /api/course-tracking/user/{userId}/metrics
   */
  async getUserMetrics(userId: string): Promise<UserCourseMetrics> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSE_TRACKING.USER_METRICS(userId));

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to fetch user metrics');
    } catch (error: any) {
      console.error('Error fetching user metrics:', error);
      throw error;
    }
  }

  /**
   * Get course overview for a specific user (Admin)
   * GET /api/course-tracking/user/{userId}/course/{courseId}/overview
   * Returns comprehensive overview including progress, time spent, assessment scores, XP details
   */
  async getUserCourseOverview(userId: string, courseId: string): Promise<any> {
    try {
      const response = await apiClient.get(`/api/course-tracking/user/${userId}/course/${courseId}/overview`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to fetch course overview');
    } catch (error: any) {
      console.error('Error fetching course overview:', error);
      throw error;
    }
  }

  /**
   * Get user certification progress (POST with filters)
   * POST /api/certification-tracking/users/{userId}/progress
   */
  async getUserCertificationProgress(
    userId: string,
    filters?: CertificationTrackingFilters
  ): Promise<PaginatedCertificationResponse | CertificationTracking> {
    try {
      const payload: CertificationTrackingFilters = {
        ...filters,
        sort_order: filters?.sort_order || 'desc'
      };

      const response = await apiClient.post(
        API_ENDPOINTS.CERTIFICATION_TRACKING.USER_PROGRESS(userId),
        payload
      );

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to fetch user certification progress');
    } catch (error: any) {
      console.error('Error fetching user certification progress:', error);
      throw error;
    }
  }

  /**
   * Get user certification metrics
   * GET /api/certification-tracking/users/{userId}/metrics
   */
  async getUserCertificationMetrics(userId: string): Promise<UserCertificationMetrics> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATION_TRACKING.USER_METRICS(userId));

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to fetch user certification metrics');
    } catch (error: any) {
      console.error('Error fetching user certification metrics:', error);
      throw error;
    }
  }

  /**
   * Mark a course as completed
   */
  async completeCourse(courseId: string): Promise<{ message: string; certificate_id?: string }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.COURSE_TRACKING.COMPLETE_COURSE_BY_ID(courseId));
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.description || 'Failed to mark course as completed');
    } catch (error: any) {
      console.error('Error completing course:', error);
      throw error;
    }
  }

  /**
   * Get course completion statistics
   */
  async getCourseStats(courseId: string): Promise<{
    course_id: string;
    total_assigned: number;
    not_started: number;
    in_progress: number;
    completed: number;
    completion_rate: number;
    average_completion_time: number;
    average_score: number;
    overdue_count: number;
  }> {
    try {
      const response = await apiClient.get(`/api/course-tracking/course/${courseId}/stats`);
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.description || 'Failed to fetch course statistics');
    } catch (error: any) {
      console.error('Error fetching course stats:', error);
      throw error;
    }
  }

  /**
   * Get assignment tracking statistics
   */
  async getAssignmentStats(assignmentId: string): Promise<TrackingSummary> {
    try {
      const response = await apiClient.get(`/api/course-tracking/assignment/${assignmentId}/stats`);
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.description || 'Failed to fetch assignment statistics');
    } catch (error: any) {
      console.error('Error fetching assignment stats:', error);
      throw error;
    }
  }

  /**
   * Generate tracking report
   */
  async generateReport(filters: {
    start_date?: string;
    end_date?: string;
    course_ids?: string[];
    user_ids?: string[];
    group_keys?: string[];
    status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
    is_mandatory?: boolean;
  }): Promise<any> {
    try {
      const response = await apiClient.post('/api/course-tracking/reports', filters);
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.description || 'Failed to generate report');
    } catch (error: any) {
      console.error('Error generating report:', error);
      throw error;
    }
  }

  /**
   * Update expired tracking records
   */
  async updateExpiredTrackings(): Promise<{ updated_count: number; message: string }> {
    try {
      const response = await apiClient.post('/api/course-tracking/update-expired');
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.description || 'Failed to update expired trackings');
    } catch (error: any) {
      console.error('Error updating expired trackings:', error);
      throw error;
    }
  }

  /**
   * Calculate time spent in hours and minutes
   */
  formatTimeSpent(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  /**
   * Calculate days until deadline
   */
  getDaysUntilDeadline(deadlineDate: string): number {
    const deadline = new Date(deadlineDate);
    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Check if deadline is approaching (within 7 days)
   */
  isDeadlineApproaching(deadlineDate: string): boolean {
    const daysUntil = this.getDaysUntilDeadline(deadlineDate);
    return daysUntil > 0 && daysUntil <= 7;
  }

  /**
   * Check if deadline is overdue
   */
  isDeadlineOverdue(deadlineDate: string): boolean {
    return this.getDaysUntilDeadline(deadlineDate) < 0;
  }

  /**
   * Bulk update complete course tracking structure
   * NEW: Uses bulk-update endpoint for complete state sync
   */
  async bulkUpdateTracking(data: any): Promise<any> {
    try {
      console.log('[Service] 📡 POST /bulk-update', {
        course_id: data.course_id,
        progress: data.progress_percentage,
        status: data.status,
        modules: data.module_progress?.length || 0
      });

      const response = await apiClient.post('/api/course-tracking/bulk-update', data);

      console.log('[Service] 📡 POST /bulk-update response:', response.data.success ? '✅ SUCCESS' : '❌ FAILED');

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.description || 'Failed to bulk update tracking');
    } catch (error: any) {
      console.error('[Service] ❌ Error bulk updating tracking:', error);
      throw error;
    }
  }
  async updateTrackingSettings(trackingId: string, data: {
    is_mandatory?: boolean;
    deadline_date?: string;
    send_notification?: boolean;
    enable_automatic_reminder?: boolean;
    reminder_frequency_days?: number | null;
  }) {
    const response = await apiClient.put(API_ENDPOINTS.COURSE_TRACKING.UPDATE_SETTINGS(trackingId), data);
    return response.data?.data || response.data;
  }

  async revokeCourseForUser(courseId: string, userId: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.COURSE_TRACKING.REVOKE(courseId, userId));
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to revoke course');
    } catch (error: any) {
      throw error;
    }
  }
}

export const trackingService = new TrackingService();
export default trackingService;